import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import utc from "dayjs/plugin/utc.js";
dayjs.extend(localeData);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
// en-IN locale data: dayjs ships en as default; we register en-IN via Intl-derived localeData override.
// dayjs's localeData plugin uses the default `en` locale; to get Indian date conventions we explicitly
// format via DD/MM/YYYY tokens regardless of locale (the tokens already encode the Indian order).
try {
    // dayjs locale registration is idempotent if the locale pack is installed; we ship only the `en`
    // locale in dayjs core, so we don't call `dayjs.locale('en-in')` — the explicit tokens below
    // already produce Indian-order output (DD/MM/YYYY).
}
catch {
    // no-op: locale pack optional
}
/**
 * Format an integer paise amount as Indian rupees, e.g. 12345678 → "₹1,23,456.78".
 * Uses the en-IN currency grouping (lakhs/crores).
 */
export const fmtINR = (paise) => new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
}).format((paise ?? 0) / 100);
/**
 * Format a paise amount for an editable input field (no symbol, two decimals).
 * e.g. 123456 → "1234.56"
 */
export const fmtINRInput = (paise) => new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
}).format((paise ?? 0) / 100);
export const fmtDate = (d) => dayjs(d).format("DD/MM/YYYY");
/**
 * Date format for PDF PRINTS (default): DD-MMM-YYYY, e.g. 11-Nov-2026.
 *
 * Used by every jsPDF renderer (prescription, visit summary, investigation,
 * invoice, certificate, referral, operational reports) so all printed
 * documents share one unambiguous date style. Uses the same dayjs parse
 * path and invalid-input behaviour as fmtDate, so date-boundary semantics
 * are identical — only the output mask differs (day zero-padded, English
 * 3-letter month abbreviation). On-screen UI (tables, dialogs, labels)
 * keeps fmtDate DD/MM/YYYY; R2 upload filenames keep fmtDate/dateIST.
 * On-screen EXCEPTION (2026-11-03, user request — post-mortem #302): the
 * EMR visit page header's last-visit review strip (last visit date +
 * review date) uses this format on-screen — DD/MM/YYYY and DD-MMM-YYYY
 * in one row reads ambiguously at a glance; the strip mirrors the printed
 * documents' style.
 */
export const fmtDatePdf = (d) => dayjs(d).format("DD-MMM-YYYY");
export const fmtDateTime = (d) => dayjs(d).format("DD/MM/YYYY hh:mm A");
export const fmtTime = (d) => dayjs(d).format("hh:mm A");
/**
 * Today's date in the Indian timezone (IST, UTC+5:30) as a YYYY-MM-DD string.
 *
 * Use this instead of `new Date().toISOString().slice(0, 10)` for "today"
 * defaults — `toISOString()` returns the UTC date, which is yesterday's date
 * when run before 5:30 AM IST (or after 6:30 PM IST). This formats the date
 * in Asia/Kolkata so the calendar key matches the user's local day.
 */
export const todayIST = () => new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
}).format(new Date());
/**
 * Format any Date (or date string / timestamp) as a YYYY-MM-DD string in
 * the Indian timezone (IST, UTC+5:30). Use this instead of
 * `new Date(x).toISOString().slice(0, 10)` — the latter returns the UTC
 * date, which is the previous calendar day between 18:30 UTC and 24:00 UTC
 * (i.e. 00:00–05:30 IST). Pass `new Date()` to get today's date in IST
 * (equivalent to `todayIST()`).
 */
export const dateIST = (d = new Date()) => new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
}).format(new Date(d));
/**
 * Convert a UI date string (DD/MM/YYYY or YYYY-MM-DD) into a JS Date at local midnight.
 * Returns null on parse failure.
 */
export const parseDateInput = (input) => {
    if (!input)
        return null;
    const d = dayjs(input, ["DD/MM/YYYY", "YYYY-MM-DD"], true);
    return d.isValid() ? d.toDate() : null;
};
/**
 * Indian phone validation. Accepts:
 *  - Mobile: exactly 10 digits, starting 6-9 (no +91 prefix).
 *  - Landline: STD code starting with 0 followed by the subscriber number
 *    (e.g. 044-28293333, 011-23456789). May exceed 10 digits total.
 * Spaces and dashes are stripped before matching. +91 prefixes are rejected.
 */
const PHONE_RE = /^(0\d{2,4}-?\d{6,8}|[6-9]\d{9})$/;
export const validatePhone = (v) => PHONE_RE.test((v ?? "").replace(/[\s-]/g, ""));
/**
 * Normalize an Indian phone input. Strips spaces/dashes; drops any leading
 * +91 / 91 / 0 country prefix that was added by mistake (we store bare
 * national numbers — 10-digit mobile, or 0-STD landline). Returns "" for empty.
 */
export const normalizePhone = (v) => {
    let cleaned = (v ?? "").replace(/[\s-]/g, "");
    if (!cleaned)
        return "";
    // Strip accidental +91 / 91 country prefix (we don't store E.164).
    if (cleaned.startsWith("+91"))
        cleaned = cleaned.slice(3);
    else if (cleaned.startsWith("91") && cleaned.length >= 12)
        cleaned = cleaned.slice(2);
    return cleaned;
};
export const validatePincode = (v) => /^[1-9]\d{5}$/.test((v ?? "").trim());
/**
 * Validate an Aadhaar reference: either the full 12-digit Aadhaar number or the
 * 16-digit Virtual ID (VID). Spaces are stripped before checking length.
 */
export const validateAadhaar = (v) => {
    const digits = (v ?? "").replace(/\s/g, "");
    return /^\d{12}$/.test(digits) || /^\d{16}$/.test(digits);
};
/**
 * Mask an Aadhaar number to keep only the last 4 digits, e.g. "1234 5678 9012" → "XXXX-XXXX-9012".
 * Accepts 12-digit or space-separated inputs. (16-digit VIDs are returned unchanged.)
 */
export const maskAadhaar = (v) => {
    const digits = (v ?? "").replace(/\D/g, "");
    if (digits.length !== 12)
        return v ?? "";
    return `XXXX-XXXX-${digits.slice(8)}`;
};
export { dayjs };
//# sourceMappingURL=india.js.map